import {FormField, useFormContext, type FormControl} from 'platform/components';
import {Grid, Show} from 'platform/foundation';

import {useState} from 'react';
import {type Path} from 'react-hook-form';

import {union} from 'ramda';

import {t} from '../catalogueUi';
import {GrossPriceField} from '../GrossPriceField';
import {missingPriceFills} from '../moneyInput';
import {type NewModelDraft} from '../newModelDraft';
import {NumberField} from '../NumberField';
import {PriceRow} from '../PriceRow';
import {useCatalogueVatRate} from '../useCatalogueVatRate';
import {trimPriceRequired} from '../utils/offeredTrimRows';

type TrimPricedForm = {
  trimPrices: Partial<Record<string, string | null>>;
  costNet: string | null;
};

/**
 * The trims a colour or package of the new model is on, its price on each chosen one and its cost.
 * The first price entered fills the chosen trims the author has not priced; each stays editable, and
 * a price the author changed, cleared included, stays theirs.
 */
export function TrimPriceFields<T extends TrimPricedForm>(props: {
  control: FormControl<T>;
  /** The form's chosen trim ids: `trimIds` on a colour, `applicableTrimIds` on a package. */
  trimIdsField: Path<T>;
  priceField: (trimId: string) => Path<T>;
  costField: Path<T>;
  trimsLabel: string;
  trimOptions: {value: string; label: string}[];
  trims: NewModelDraft['trims'];
  selectedTrimIds: string[];
  itemName: string;
  canViewCost: boolean;
}) {
  const form = useFormContext<TrimPricedForm>();
  const vatRate = useCatalogueVatRate();
  const [editedTrimIds, setEditedTrimIds] = useState<string[]>([]);
  const chosenTrims = props.trims.filter(({id}) => props.selectedTrimIds.includes(id));
  const prefillPrices = (selectedIds: string[]) => {
    const ids = props.trims.map(({id}) => id).filter((id) => selectedIds.includes(id));
    Object.entries(missingPriceFills(form.getValues().trimPrices, ids))
      .filter(([id]) => !editedTrimIds.includes(id))
      .forEach(([id, price]) => form.setValue(`trimPrices.${id}`, price, {shouldDirty: true}));
  };
  return (
    <>
      <FormField
        control={props.control}
        name={props.trimIdsField}
        type="checkboxes"
        direction="vertical"
        label={props.trimsLabel}
        options={props.trimOptions}
        isRequired
        onChange={(ids) => prefillPrices(ids ?? [])}
      />
      {chosenTrims.map((trim) => (
        <PriceRow
          key={trim.id}
          vatRate={vatRate}
          canViewCost={false}
          spacing={4}
          stackBelow="notebook"
        >
          <GrossPriceField
            control={props.control}
            name={props.priceField(trim.id)}
            vatRate={vatRate}
            label={t(
              'entity.newVehicle.catalogue.priceOnTrimNet',
              'Price of {feature} on {trim}, net',
              {feature: props.itemName, trim: trim.name}
            )}
            helperText={trimPriceRequired()}
            isRequired
            onChange={() => setEditedTrimIds((ids) => union(ids, [trim.id]))}
            onBlur={() => prefillPrices(props.selectedTrimIds)}
          />
        </PriceRow>
      ))}
      <Show when={props.canViewCost && chosenTrims.length > 0}>
        <Grid columns={[1, 1, 2]} spacing={4}>
          <NumberField
            control={props.control}
            name={props.costField}
            label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
          />
        </Grid>
      </Show>
    </>
  );
}
